import {api} from '@/services/api.js'

/**
 * The v3 redeem every scan screen makes (POST /api/v3/scan/*), as the rep
 * should see it. A duplicate (the same scan sent twice, e.g. a retry after a
 * lost response) is answered with the first attempt's outcome, so it is shown
 * as that outcome rather than as an error. A refusal is worded here from its
 * reason code. A request with no answer gives up after SCAN_TIMEOUT_MS so the
 * scanner is never left blocked.
 */
export const SCAN_TIMEOUT_MS = 15000

const dict = () => (typeof __DICT__ !== 'undefined' ? __DICT__.scan?.refusals : null) || {}

/** English defaults; a brand dictionary can override any under scan.refusals. */
const DEFAULT_MESSAGES = {
    ticket_not_found: 'No ticket found for this guest here',
    ticket_deleted: 'This ticket has been deleted. Check the guest’s booking before going ahead.',
    already_redeemed: 'Already scanned for this guest',
    room_blocked: 'This room is blocked',
    room_not_ready: 'The keys for this room are not ready yet',
    reference_not_allowed: 'This guest cannot collect the keys for this room',
    pending: 'This scan is still being processed. Check the list before scanning again.',
    unknown: 'This scan was sent before and its outcome is not known. Check the list before scanning again.',
    timeout: 'No answer from the server. Check the list before scanning again.',
}

export const REFUSAL_MESSAGES = Object.freeze({...DEFAULT_MESSAGES, ...dict()})

// A refusal, or an outcome the rep has to check (pending, unknown, timeout).
function refusal(code, serverError, extra = {}) {
    const message = REFUSAL_MESSAGES[code] ?? serverError?.message ?? 'Scan refused'
    return Object.assign(new Error(message), extra, {code})
}

/**
 * @param {string} endpoint a /api/v3/scan route
 * @param {object} payload
 * @param {object} [options]
 * @param {number} [options.timeoutMs]
 * @param {Function} [options.send] for tests
 * @returns {Promise<{outcome: string, duplicate: boolean, response: object|null, original?: object}>}
 *   original: a duplicate's first answer
 *   throws an Error with `code` (the reason) and a message to show
 */
export async function redeemScan(endpoint, payload, {timeoutMs = SCAN_TIMEOUT_MS, send = (e, p, o) => api.post(e, p, o)} = {}) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)
    try {
        const response = await send(endpoint, payload, {signal: controller.signal})
        return {outcome: response?.outcome ?? 'accepted', duplicate: false, response}
    } catch (error) {
        if (controller.signal.aborted) throw refusal('timeout', error)
        if (error?.code !== 'duplicate') {
            if (!error?.code) throw error
            throw refusal(error.code, error, {lastUsed: error.lastUsed, qrVerification: error.qrVerification, status: error.status})
        }
        const original = error.original
        // Only the scan log has this key (pruned, or sent to an old route): the
        // first attempt's outcome is not known (docs/adr/0001).
        if (!original) throw refusal('unknown', error)
        if (original.outcome === 'pending') throw refusal('pending', error)
        if (original.outcome === 'refused') throw refusal(original.code, {message: original.error})
        return {outcome: original.outcome, duplicate: true, response: null, original}
    } finally {
        clearTimeout(timer)
    }
}
